import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Check, Building2, Globe, CreditCard } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { MaskedLines, Reveal, SectionHead } from '../components/motion';
import LeadModal from '../components/LeadModal';

const IMG_CHART = 'https://images.unsplash.com/photo-1689732888407-310424e3a372?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHx0cmFkaW5nJTIwZmxvb3IlMjBmaW5hbmNpYWwlMjBleGNoYW5nZSUyMHN0b2NrJTIwbWFya2V0JTIwY2hhcnQlMjBkYXJrfGVufDB8fHx8MTc5MDA2ODMxN3ww&ixlib=rb-4.1.0&q=85';

export default function Selection() {
  const { t } = useLang();
  const [leadOpen, setLeadOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const openLead = (p = null) => { setPlan(p); setLeadOpen(true); };
  const goToPayment = () => { window.location.href = plan ? `/selection-payment?plan=${plan.key}` : '/selection-payment'; };
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const metrics = t('selection.metrics');
  const steps = t('selection.steps');
  const conditions = t('selection.conditions');
  const benefits = t('home.benefits');
  const plans = t('selection.plans');

  return (
    <main data-testid="selection-page">
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center pt-28 pb-20 overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={IMG_CHART} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/75 via-[#0F172A]/90 to-[#0F172A]" />
        </motion.div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-4 py-2 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 pulse-dot" />
            <span className="text-xs font-semibold text-green-300">{t('selection.overline')}</span>
          </motion.div>
          <MaskedLines
            lines={t('selection.heroLines')}
            colors={['text-white', 'text-green-400', 'text-blue-400']}
            className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[1.02]"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 max-w-2xl mx-auto text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="selection-hero-sub"
          >
            {t('selection.heroSub')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
          >
            <button
              data-testid="selection-apply-btn"
              onClick={() => openLead()}
              className="mt-9 inline-flex items-center gap-2 rounded-lg bg-green-500 px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-green-400 hover:shadow-[0_0_35px_rgba(34,197,94,0.5)] hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {t('selection.ctaBtn')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-slate-800 bg-[#111D36]" data-testid="selection-metrics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {metrics.map((m, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid={`selection-metric-${i}`} className="rounded-xl border border-slate-700/70 bg-[#1E293B] p-6 text-center">
                <p className="font-display text-3xl sm:text-4xl text-blue-400">{m.v}</p>
                <p className="text-xs font-semibold text-slate-300 mt-2">{m.l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="selection-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('selection.stepsOverline')} title={t('selection.stepsTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div data-testid={`selection-step-${i}`} className="group relative h-full rounded-xl border border-slate-700/70 bg-[#1E293B] p-8 overflow-hidden transition-all duration-300 hover:border-blue-500/60 hover:-translate-y-1">
                  <span className="absolute -top-4 right-4 font-display text-8xl text-white/5 group-hover:text-blue-400/15 transition-colors">{s.num}</span>
                  <h3 className="relative font-display text-2xl text-white">{s.t}</h3>
                  <p className="relative mt-3 text-sm text-slate-400 leading-relaxed max-w-md">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="selection-conditions">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <SectionHead overline={t('selection.condOverline')} title={t('selection.condTitle')} />
            <ul className="mt-10 space-y-4">
              {conditions.map((c, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <li className="flex items-start gap-3 text-slate-300">
                    <span className="mt-1 flex w-5 h-5 shrink-0 items-center justify-center rounded-full bg-blue-500/15 border border-blue-500/40">
                      <Check className="w-3 h-3 text-blue-400" />
                    </span>
                    <span className="text-sm sm:text-base">{c}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <SectionHead overline={t('selection.benOverline')} title={t('selection.benTitle')} />
            <div className="mt-10 space-y-5">
              {benefits.map((b, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div data-testid={`selection-benefit-${i}`} className="rounded-xl border border-slate-700/70 bg-[#1E293B] p-6 transition-colors duration-300 hover:border-green-500/50">
                    <h3 className="font-display text-lg text-white">{b.t}</h3>
                    <p className="mt-2 text-sm text-slate-400">{b.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="selection-pricing">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <SectionHead overline={t('selection.priceOverline')} title={t('selection.priceTitle')} align="center" />
          <div className="mt-12 grid gap-6 md:grid-cols-2 items-stretch">
            {plans.map((plan, i) => (
              <Reveal key={plan.key} delay={0.1 + i * 0.1} className="h-full">
                <div
                  data-testid={`selection-plan-${plan.key}`}
                  className={`relative h-full flex flex-col rounded-2xl border p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1.5 ${
                    plan.badge
                      ? 'border-green-500/70 bg-[#1E293B] shadow-[0_0_45px_rgba(34,197,94,0.15)]'
                      : 'border-slate-700/70 bg-[#1E293B] hover:border-slate-500'
                  }`}
                >
                  {plan.badge && (
                    <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-green-500 px-3 py-1 text-[10px] font-bold tracking-widest text-white uppercase">
                      <Building2 className="w-3 h-3" />
                      {t('common.popular')}
                    </span>
                  )}
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                    {plan.key === 'office' ? <Building2 className="w-4 h-4 text-green-400" /> : <Globe className="w-4 h-4 text-blue-400" />}
                    {plan.name}
                  </div>
                  <p className="mt-4 font-display text-5xl sm:text-6xl text-white" data-testid={`selection-price-${plan.key}`}>{plan.price}</p>
                  <p className="mt-2 text-xs font-semibold tracking-widest text-slate-500">{plan.note}</p>
                  <ul className="mt-8 space-y-3.5 text-left flex-1">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-slate-300">
                        <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-400" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-xs text-slate-500 leading-relaxed">{plan.fit}</p>
                  <button
                    data-testid={`selection-pay-btn-${plan.key}`}
                    onClick={() => openLead(plan)}
                    className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-lg px-8 py-4 text-lg font-semibold transition-all duration-300 active:scale-[0.98] ${
                      plan.badge
                        ? 'bg-green-500 text-white hover:bg-green-400 hover:shadow-[0_0_35px_rgba(34,197,94,0.5)]'
                        : 'border border-slate-600 text-slate-100 hover:border-green-500/60 hover:text-green-300'
                    }`}
                  >
                    <ArrowRight className="w-5 h-5" />
                    {t('selection.priceBtn')}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-500 text-center" data-testid="selection-pay-methods">
              <CreditCard className="w-4 h-4 text-green-400 shrink-0" />
              {t('selection.payMethods')}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-gradient-to-r from-green-700 via-green-600 to-emerald-700 relative overflow-hidden" data-testid="selection-cta">
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl text-white">{t('selection.ctaTitle')}</h2>
            <p className="mt-5 text-green-100">{t('selection.ctaSub')}</p>
            <button
              data-testid="selection-cta-apply-btn"
              onClick={() => openLead()}
              className="mt-9 inline-flex items-center gap-2 rounded-lg bg-white px-9 py-4 text-lg font-semibold text-green-700 transition-all duration-300 hover:bg-green-50 hover:shadow-[0_0_45px_rgba(255,255,255,0.35)] hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {t('selection.ctaBtn')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </Reveal>
        </div>
      </section>

      <LeadModal open={leadOpen} onOpenChange={setLeadOpen} source={plan ? `selection: ${plan.name}` : 'selection'} onSuccess={goToPayment} />
    </main>
  );
}
